/**
 * ExecHome — Landing page for Overmatch Exec.
 *
 * Shows suggested actions that either:
 *   - Navigate to an exec page (goals, portfolios, dashboard, explore)
 *   - Start an agent conversation with a workflow script
 *   - Open the agent drawer with a pre-filled message
 *
 * Also shows a chat input for free-form questions to the exec agent.
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Target, FolderOpen, Compass,
  Sparkles, ArrowRight, MessageSquare,
} from 'lucide-react'
import { useAgent } from '../contexts/AgentContext'
import { useAgentChat, type ViewSuggestion } from '../hooks/useAgentChat'
import { useStickToBottom } from '../hooks/useStickToBottom'
import { JumpToLatest } from '../components/ui'

interface ActionCard {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  color: string
  /** If set, navigates to this path */
  navigateTo?: string
  /** If set, sends this message to the agent (and optionally activates a script) */
  agentMessage?: string
  scriptId?: string
}

const ACTIONS: ActionCard[] = [
  {
    id: 'setup',
    icon: <Sparkles size={20} />,
    title: 'Set up Exec',
    description: 'Walk through configuring your portfolio, metrics, goals, and dashboard with AI guidance.',
    color: 'from-violet-500 to-purple-600',
    agentMessage: 'Help me set up my executive dashboard.',
    scriptId: 'exec_setup',
  },
  {
    id: 'portfolio',
    icon: <FolderOpen size={20} />,
    title: 'Define your portfolio',
    description: 'Select which offices and MAJCOMs you oversee to scope your metrics.',
    color: 'from-blue-500 to-cyan-600',
    navigateTo: '/exec/portfolio-groups',
  },
  {
    id: 'goals',
    icon: <Target size={20} />,
    title: 'Set a performance goal',
    description: 'Define targets for speed, competition, small business, or other metrics.',
    color: 'from-emerald-500 to-teal-600',
    navigateTo: '/exec/goals',
  },
  {
    id: 'explore',
    icon: <Compass size={20} />,
    title: 'Explore the data',
    description: 'Interactively explore acquisition performance across any dimension.',
    color: 'from-amber-500 to-orange-600',
    navigateTo: '/exec/explore',
  },
  {
    id: 'dashboard',
    icon: <LayoutDashboard size={20} />,
    title: 'Go to dashboard',
    description: 'View your configured widgets, charts, and KPIs.',
    color: 'from-slate-500 to-gray-600',
    navigateTo: '/exec/dashboard',
  },
]

export default function ExecHome() {
  const navigate = useNavigate()
  const { pageContext, conversationId, setConversationId, openDrawer } = useAgent()
  const [input, setInput] = useState('')
  const [showChat, setShowChat] = useState(false)
  const [logoExpanded, setLogoExpanded] = useState(false)
  const { containerRef, contentRef, showJumpToLatest, jumpToLatest } = useStickToBottom()

  const {
    messages,
    isStreaming,
    sendMessage,
  } = useAgentChat({
    conversationId,
    onViewSuggestion: (suggestion: ViewSuggestion) => {
      if (suggestion.autoNavigate && suggestion.view) {
        navigate(suggestion.view)
      }
    },
    onConversationCreated: (newId: string) => {
      setConversationId(newId)
      setShowChat(true)
    },
  })

  useEffect(() => {
    setConversationId(null)
    setShowChat(false)
    const timer = setTimeout(() => setLogoExpanded(true), 500)
    return () => clearTimeout(timer)
  }, [])

  const handleAction = (action: ActionCard) => {
    if (action.navigateTo) {
      navigate(action.navigateTo)
      // If it's a page that benefits from the agent, open the drawer
      if (action.id === 'portfolio') {
        setTimeout(() => openDrawer(), 300)
      }
    } else if (action.agentMessage) {
      setShowChat(true)
      sendMessage(action.agentMessage, pageContext, action.scriptId)
    }
  }

  const handleSend = () => {
    if (!input.trim() || isStreaming) return
    setShowChat(true)
    sendMessage(input, pageContext)
    setInput('')
    // Sending is an explicit request to see what comes back.
    jumpToLatest()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // ── Chat view (after an action or message) ──────────────────────
  if (showChat) {
    return (
      <div className="flex flex-col h-full">
        <div className="relative flex-1 min-h-0 flex flex-col">
          <div
            ref={containerRef}
            className="flex-1 min-h-0 overflow-y-auto px-4 py-6 max-w-3xl mx-auto w-full"
          >
            <div ref={contentRef}>
              {messages.map((msg, i) => (
                <div key={i} className={`mb-4 ${msg.role === 'user' ? 'text-right' : ''}`}>
                  <div
                    className={`inline-block max-w-[80%] px-4 py-2 rounded-xl text-sm whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {isStreaming && (
                <div className="mb-4">
                  <div className="inline-block px-4 py-2 rounded-xl text-sm bg-gray-100 dark:bg-gray-800 text-gray-500">
                    <span className="animate-pulse">Thinking...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <JumpToLatest visible={showJumpToLatest} onClick={jumpToLatest} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="max-w-3xl mx-auto flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your portfolio performance..."
              rows={1}
              className="flex-1 px-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isStreaming}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <MessageSquare size={16} />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Landing view ────────────────────────────────────────────────
  const setupAction = ACTIONS.find((a) => a.id === 'setup')!
  const gridActions = ACTIONS.filter((a) => a.id !== 'setup')

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header with animated logo reveal */}
      <div className="px-8 pt-8 pb-4 shrink-0">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center mb-2 -ml-3">
            <img
              src="/o_logo_navy.png"
              alt="Overmatch"
              className="h-16 -mr-3.5 dark:hidden"
            />
            <img
              src="/o_logo_white.png"
              alt="Overmatch"
              className="h-16 -mr-3.5 hidden dark:block"
            />
            <div
              className="overflow-hidden transition-all duration-[1500ms]"
              style={{ maxWidth: logoExpanded ? 500 : 0, transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.05, 1)' }}
            >
              <span className="text-5xl font-extrabold tracking-tight text-foreground whitespace-nowrap block">
                VERMATCH
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-8 pt-2 pb-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Chat input */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything about acquisition performance..."
                rows={2}
                className="w-full px-5 py-4 text-sm rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm pr-14"
                autoFocus
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="absolute right-3 bottom-3 p-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-30 transition-colors"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {/* Featured Setup card — full width */}
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => handleAction(setupAction)}
              className="w-full text-left p-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:shadow-lg hover:border-violet-300 dark:hover:border-violet-700 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${setupAction.color} text-white shrink-0`}>
                  {setupAction.icon}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 group-hover:text-violet-600 transition-colors">
                    {setupAction.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {setupAction.description}
                  </p>
                </div>
                <ArrowRight size={20} className="text-gray-300 group-hover:text-violet-500 ml-auto shrink-0 transition-colors" />
              </div>
            </button>
          </div>

          {/* 2x2 grid for other actions */}
          <div className="max-w-2xl mx-auto grid grid-cols-2 gap-3">
            {gridActions.map((action) => (
              <button
                key={action.id}
                onClick={() => handleAction(action)}
                className="text-left p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all group"
              >
                <div className={`inline-flex p-2 rounded-lg bg-gradient-to-br ${action.color} text-white mb-3`}>
                  {action.icon}
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1 group-hover:text-blue-600 transition-colors">
                  {action.title}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {action.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
